/**
 * Client-lib voor berichten — typed fetchers + types + Error class.
 *
 * Types spiegelen `api/berichten/_types.ts`. tsconfig.app `include` is alleen
 * `src/`, dus we kunnen niet rechtstreeks importeren. Bij wijzigingen: spiegel
 * dit bestand én `api/berichten/_types.ts`.
 */

import { supabase } from './supabase';
import { processImageForUpload } from './image';

// ─── Types ───────────────────────────────────────────────────────────────────

export type Richting =
  | 'office_to_field'
  | 'field_to_office'
  | 'office_to_reparateur'
  | 'reparateur_to_office';
export type Status = 'open' | 'afgehandeld';
export type Prioriteit = 'normaal' | 'urgent';

export interface BerichtSummary {
  id: string;
  werkOrderId: string | null;
  werkOrderName: string | null;
  wasserijItemId: string | null;
  wasserijItemName: string | null;
  reparatieOpdrachtId: string | null;
  reparatieOpdrachtNaam: string | null;
  customerName: string | null;
  onderwerp: string | null;
  categorie: string | null;
  prioriteit: Prioriteit | string;
  status: Status | string;
  richting: Richting;
  bron: string;
  aangemaaktDoorEmail: string;
  aangemaaktDoorNaam: string | null;
  gelezenAt: string | null;
  afgehandeldDoorEmail: string | null;
  afgehandeldAt: string | null;
  afgehandeldNote: string | null;
  fotoUrls: string[];
  laatsteReactiePreview: string | null;
  laatsteReactieAt: string | null;
  aantalReacties: number;
  createdAt: string;
  updatedAt: string;
}

export interface Reactie {
  id: string;
  berichtId: string;
  richting: Richting;
  body: string;
  fotoUrls: string[];
  bron: string;
  auteurEmail: string;
  auteurNaam: string | null;
  createdAt: string;
}

export interface BerichtDetail extends BerichtSummary {
  reacties: Reactie[];
}

export interface CreateBerichtInput {
  werkOrderId?: string | null;
  wasserijItemId?: string | null;
  reparatieOpdrachtId?: string | null;
  onderwerp?: string | null;
  categorie?: string | null;
  prioriteit?: Prioriteit;
  body: string;
  fotoUrls?: string[];
  richting?: Richting;
}

export interface CreateBerichtResult {
  berichtId: string;
  bericht: BerichtSummary;
}

export interface ReplyInput {
  berichtId: string;
  body: string;
  fotoUrls?: string[];
  richting?: Richting;
}

export interface ReplyResult {
  reactieId: string;
  status: Status;
}

export interface ResolveInput {
  berichtId: string;
  note?: string;
}

export interface ReopenInput {
  berichtId: string;
  reden?: string;
}

export interface BerichtListFilters {
  status?: 'open' | 'afgehandeld' | 'all';
  richting?: Richting;
  /** Groepeert beide richtingen van één gesprekspartner (overschrijft `richting`). */
  peer?: 'field' | 'reparateur';
  werkOrderId?: string;
  wasserijItemId?: string;
  reparatieOpdrachtId?: string;
  search?: string;
  limit?: number;
}

export interface BerichtCounts {
  open: number;
  total: number;
}

export interface FotoUploadResult {
  path: string;
  url: string;
}

// ─── UI-labels ─────────────────────────────────────────────────────────────

export const RICHTING_LABELS: Record<Richting, string> = {
  office_to_field: 'Naar uitvoering',
  field_to_office: 'Van uitvoering',
  office_to_reparateur: 'Naar reparateur',
  reparateur_to_office: 'Van reparateur',
};

export const STATUS_LABELS: Record<Status, string> = {
  open: 'Open',
  afgehandeld: 'Afgehandeld',
};

// ─── Error class ─────────────────────────────────────────────────────────────

export class BerichtError extends Error {
  errorCode?: string;
  status: number;
  constructor(message: string, status = 400, errorCode?: string) {
    super(message);
    this.name = 'BerichtError';
    this.status = status;
    this.errorCode = errorCode;
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

async function parseError(res: Response, fallback: string): Promise<BerichtError> {
  const body = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
  return new BerichtError(body?.error || fallback, res.status, body?.code);
}

async function postJson<T>(path: string, body: unknown, fallbackError: string): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseError(res, fallbackError);
  return res.json() as Promise<T>;
}

async function getJson<T>(path: string, fallbackError: string): Promise<T> {
  const res = await fetch(path, { headers: await authHeaders() });
  if (!res.ok) throw await parseError(res, fallbackError);
  return res.json() as Promise<T>;
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function createBericht(input: CreateBerichtInput): Promise<CreateBerichtResult> {
  return postJson('/api/berichten/create', input, 'Bericht aanmaken mislukt');
}

export function replyBericht(input: ReplyInput): Promise<ReplyResult> {
  return postJson('/api/berichten/reply', input, 'Reactie mislukt');
}

export function resolveBericht(
  input: ResolveInput
): Promise<{ status: Status; afgehandeldAt?: string; message?: string }> {
  return postJson('/api/berichten/resolve', input, 'Afhandelen mislukt');
}

export function reopenBericht(
  input: ReopenInput
): Promise<{ status: Status; message?: string }> {
  return postJson('/api/berichten/reopen', input, 'Heropenen mislukt');
}

// ─── Reads ───────────────────────────────────────────────────────────────────

export async function fetchBerichtList(filters: BerichtListFilters = {}): Promise<BerichtSummary[]> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.peer) qs.set('peer', filters.peer);
  if (filters.richting) qs.set('richting', filters.richting);
  if (filters.werkOrderId) qs.set('werkOrderId', filters.werkOrderId);
  if (filters.wasserijItemId) qs.set('wasserijItemId', filters.wasserijItemId);
  if (filters.search) qs.set('search', filters.search);
  if (filters.limit) qs.set('limit', String(filters.limit));
  const result = await getJson<{ berichten: BerichtSummary[] }>(
    `/api/berichten/list?${qs.toString()}`,
    'Berichten-lijst ophalen mislukt'
  );
  return result.berichten;
}

/**
 * `inbound` (default in de API) telt alles dat kantoor moet oppakken:
 * field_to_office én reparateur_to_office.
 */
export function fetchBerichtCounts(richting?: Richting | 'inbound'): Promise<BerichtCounts> {
  const qs = richting ? `?richting=${richting}` : '';
  return getJson<BerichtCounts>(`/api/berichten/count${qs}`, 'Counts ophalen mislukt');
}

export function fetchBerichtDetail(id: string): Promise<BerichtDetail> {
  return getJson(`/api/berichten/detail?id=${encodeURIComponent(id)}`, 'Detail ophalen mislukt');
}

export async function fetchBerichtenByWerkorder(
  werkOrderId: string,
  wasserijItemId?: string,
  includeClosed = true
): Promise<BerichtSummary[]> {
  const qs = new URLSearchParams({ werkOrderId });
  if (wasserijItemId) qs.set('wasserijItemId', wasserijItemId);
  qs.set('includeClosed', includeClosed ? '1' : '0');
  const result = await getJson<{ berichten: BerichtSummary[] }>(
    `/api/berichten/by-werkorder?${qs.toString()}`,
    'Berichten voor werkorder ophalen mislukt'
  );
  return result.berichten;
}

export async function fetchBerichtenByReparatie(
  reparatieOpdrachtId: string,
  includeClosed = true
): Promise<BerichtSummary[]> {
  const qs = new URLSearchParams({ reparatieOpdrachtId });
  qs.set('includeClosed', includeClosed ? '1' : '0');
  const result = await getJson<{ berichten: BerichtSummary[] }>(
    `/api/berichten/by-reparatie?${qs.toString()}`,
    'Berichten voor reparatie-opdracht ophalen mislukt'
  );
  return result.berichten;
}

// ─── Foto-upload ─────────────────────────────────────────────────────────────

export async function uploadBerichtFoto(file: File, berichtId?: string): Promise<FotoUploadResult> {
  // Verklein client-side zodat de upload-body ruim onder de Vercel-limiet (~4,5MB)
  // blijft — telefoonfoto's zijn anders te groot (base64 +33%).
  const img = await processImageForUpload(file);
  return postJson<FotoUploadResult>(
    '/api/berichten/upload-foto',
    {
      filename: img.filename,
      contentType: img.contentType,
      base64: img.base64,
      berichtId: berichtId ?? null,
    },
    'Foto-upload mislukt'
  );
}

export async function refreshFotoUrl(path: string): Promise<string> {
  const { url } = await getJson<{ url: string }>(
    `/api/berichten/foto-url?path=${encodeURIComponent(path)}`,
    'Signed URL ophalen mislukt'
  );
  return url;
}
